import * as React from 'react';

import { Box, Typography } from '@mui/material';

import { Form, Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

import { venueConnectVenueApi } from '../../../api/venue/venueConnectVenueApi';
import { switchCurrentVenue } from '../../../utils/current-venue';
import LoadingButton from '../../Button/LoadingButton';
import FormikTextField from '../../Formiks/TextField/TextField';
import BaseDialog, { BaseDialogProps } from '../Base';

type AddProfileDialogProps = {
  masterVenueID: string;
} & BaseDialogProps;

type FormFields = {
  email: string;
  password: string;
};

const addProfileSchema = yup.object({
  email: yup.string().email('Invalid email address').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const AddProfileDialog: React.FC<AddProfileDialogProps> = ({ masterVenueID, ...restProps }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = async ({ email, password }: FormFields) => {
    try {
      setIsLoading(true);
      const result = await venueConnectVenueApi({ username: email.trim(), password });

      if (result?.connectedVenueID) {
        // Switch straight to the newly connected profile (full reload)
        switchCurrentVenue(result.connectedVenueID, masterVenueID);
      } else {
        setIsLoading(false);
        enqueueSnackbar({
          message: result?.message || 'Unable to connect this profile',
          variant: 'error',
        });
      }
    } catch (error) {
      setIsLoading(false);
      const _err = error as Error;
      enqueueSnackbar({ message: _err.message, variant: 'error' });
      console.error(error);
    }
  };

  return (
    <BaseDialog
      width='480px'
      height='auto'
      titleComponent={
        <Typography fontSize='20px' fontWeight={600}>
          Add profile
        </Typography>
      }
      {...restProps}
    >
      <Typography color='text.secondary' fontSize='14px' mb={3}>
        Sign in with the credentials of the business profile you want to connect. Once connected,
        you can switch between profiles from the top bar.
      </Typography>

      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={addProfileSchema}
        onSubmit={onSubmit}
      >
        {({ values, handleChange, handleBlur }) => (
          <Form>
            <Box display='flex' flexDirection='column' gap='1rem'>
              <FormikTextField
                id='add-profile-email'
                type='email'
                name='email'
                label='Email'
                placeholder='Email Address'
                autoComplete='off'
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <FormikTextField
                id='add-profile-password'
                name='password'
                label='Password'
                placeholder='Password'
                type='password'
                autoComplete='new-password'
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <LoadingButton
                type='submit'
                loading={isLoading}
                color='primary'
                fullWidth
                variant='contained'
                size='large'
                sx={{ mt: 1, mb: 2 }}
              >
                {isLoading ? 'Connecting' : 'Connect profile'}
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </BaseDialog>
  );
};

export default AddProfileDialog;
